/**
 * Bundle Cart Interactions
 *
 * Handles two bundle-specific cart behaviors:
 *
 * 1. Quantity increment (+) on a bundled item:
 *    Instead of incrementing the bundled line item's quantity,
 *    adds a new standalone line item (same variant, no bundle properties).
 *
 * 2. Remove a bundled item:
 *    After removal, strips all _bundle_* properties from remaining
 *    siblings that shared the same _bundle_key, so they become
 *    standalone items (and the "Bundle Discount" label disappears).
 *
 * Loaded in cart-drawer.liquid, runs after DOM is ready.
 */
(function () {
  'use strict';

  const BUNDLE_PROPERTIES_BLANK = {
    _bundle_id: '',
    _bundle_name: '',
    _bundle_key: '',
    _bundle_size: '',
  };

  /**
   * Build the cart API base URL (locale-aware).
   */
  function cartUrl(endpoint) {
    const root = window.Shopify?.routes?.root || '/';
    return root + endpoint;
  }

  /**
   * Refresh the cart drawer and cart page (if present).
   */
  function refreshCart() {
    document.dispatchEvent(new CustomEvent('nova:cart:refresh'));
    if (document.querySelector('#CartPage')) {
      document.dispatchEvent(new CustomEvent('nova:cartPage:refresh'));
    }
  }

  /* ──────────────────────────────────────
   * 1. Quantity increment intercept
   *
   * nova-quantity-input dispatches "changeQuantityStarted" with
   * { detail: { value: newQty, key: lineItemKey } }.
   *
   * We listen in the CAPTURE phase so we run before the existing
   * handler in component-mini-cart.js. If the item is bundled and
   * quantity is increasing, we stop the event, add a standalone
   * item instead, and refresh the cart.
   * ────────────────────────────────────── */
  document.addEventListener(
    'changeQuantityStarted',
    function (event) {
      const target = event.target;
      if (!target) return;

      // Only intercept bundled items
      const bundleVariantId = target.getAttribute('data-bundle-variant-id');
      if (!bundleVariantId) return;

      const oldQuantity = parseInt(target.getAttribute('initial'), 10) || 1;
      const newQuantity = parseInt(event.detail?.value, 10);
      if (isNaN(newQuantity)) return;

      // Increment (+ button): add standalone item instead
      if (newQuantity > oldQuantity) {
        event.stopImmediatePropagation();
        addStandaloneItem(parseInt(bundleVariantId, 10));
        return;
      }

      // Decrement to 0 (safety net): treat as bundle remove
      if (newQuantity <= 0) {
        const cartItem = target.closest('.cart-item[data-bundle-key]');
        if (cartItem) {
          event.stopImmediatePropagation();
          removeBundledItem(event.detail.key, cartItem.dataset.bundleKey);
        }
      }

      // Normal decrement (e.g. 2 → 1): let existing handler proceed
    },
    true // capture phase
  );

  /**
   * Add a standalone line item for the given variant ID.
   * If a standalone (non-bundled) item for this variant already exists
   * in the cart, increment its quantity instead of creating a duplicate.
   */
  async function addStandaloneItem(variantId) {
    try {
      // Check cart for an existing standalone item with this variant
      const cartResponse = await fetch(cartUrl('cart.js'), {
        headers: { Accept: 'application/json' },
      });
      const cartData = await cartResponse.json();

      const existing = cartData.items.find(function (item) {
        return (
          item.variant_id === variantId &&
          (!item.properties || !item.properties._bundle_key)
        );
      });

      if (existing) {
        // Increment existing standalone item
        await fetch(cartUrl('cart/change.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: existing.key,
            quantity: existing.quantity + 1,
          }),
        });
      } else {
        // No standalone item yet — add one
        await fetch(cartUrl('cart/add.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
          }),
        });
      }
    } catch (error) {
      console.error('[BundleCart] Add standalone item error:', error);
    }

    refreshCart();
  }

  /* ──────────────────────────────────────
   * 2. Remove bundled item + strip sibling properties
   *
   * We listen for clicks on .cart-item__remove-link in the
   * CAPTURE phase. If the remove button belongs to a bundled item,
   * we handle the full flow ourselves:
   *   a) Remove the item (quantity → 0)
   *   b) Fetch cart to find siblings with the same _bundle_key
   *   c) Strip _bundle_* properties from each sibling
   *   d) Refresh cart
   * ────────────────────────────────────── */
  document.addEventListener(
    'click',
    function (event) {
      const removeLink = event.target.closest('.cart-item__remove-link');
      if (!removeLink) return;

      const removeButton = removeLink.closest('.cart-item__remove');
      if (!removeButton) return;

      // Only intercept bundled items
      const bundleKey = removeButton.dataset.bundleKey;
      if (!bundleKey) return;

      // Prevent default navigation and stop the normal remove handler
      event.preventDefault();
      event.stopImmediatePropagation();

      const itemKey = removeButton.dataset.key;
      if (!itemKey) return;

      removeBundledItem(itemKey, bundleKey);
    },
    true // capture phase
  );

  /**
   * Remove a bundled item and strip bundle properties from its siblings.
   *
   * Optimizations vs naive approach:
   * - Uses the /cart/change.js remove response (returns full cart),
   *   skipping the separate /cart.js fetch.
   * - Strips sibling properties in parallel via Promise.all
   *   instead of sequential awaits.
   *
   * 2-product bundle: 2 API calls (~600ms) instead of 3 sequential (~1200ms).
   */
  async function removeBundledItem(itemKey, bundleKey) {
    try {
      // a) Remove the item — response includes the updated cart
      const removeResponse = await fetch(cartUrl('cart/change.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: itemKey, quantity: 0 }),
      });

      if (!removeResponse.ok) {
        console.error('[BundleCart] Failed to remove item');
        refreshCart();
        return;
      }

      // b) Find siblings from the remove response (no extra fetch)
      const cartData = await removeResponse.json();
      const siblings = cartData.items.filter(function (item) {
        return item.properties && item.properties._bundle_key === bundleKey;
      });

      // c) Strip _bundle_* properties from all siblings in parallel
      if (siblings.length > 0) {
        await Promise.all(
          siblings.map(function (sibling) {
            return fetch(cartUrl('cart/change.js'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({
                id: sibling.key,
                quantity: sibling.quantity,
                properties: Object.assign(
                  {},
                  sibling.properties,
                  BUNDLE_PROPERTIES_BLANK
                ),
              }),
            });
          })
        );
      }
    } catch (error) {
      console.error('[BundleCart] Remove bundled item error:', error);
    }

    // d) Refresh cart UI
    refreshCart();
  }
})();
